// SPDX-License-Identifier: AGPL-3.0-or-later

import type {
	CompanionAudioSource,
	CompanionPreferences,
	CompanionScene,
	CompanionSessionMode,
	CompanionSessionStatus,
	CompanionSpeakerRole,
	CompanionSuggestRequestOptions,
} from '@app/features/companion/commands/CompanionCommands';
import * as CompanionCommands from '@app/features/companion/commands/CompanionCommands';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import {i18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {makeAutoObservable, runInAction} from 'mobx';

export const COMPANION_TRANSCRIPT_LIMIT = 200;
export const COMPANION_SUGGESTION_LIMIT = 50;

const COMPANION_REQUEST_FAILED_DESCRIPTOR = msg({
	message: 'Companion request failed',
	comment: 'Generic error shown when a companion panel request fails without a more specific message.',
});

export type {
	CompanionPreferences,
	CompanionScene,
	CompanionSessionMode,
	CompanionSessionStatus,
	CompanionSpeakerRole,
	CompanionSuggestRequestOptions,
};

export interface CompanionSpeaker {
	speaker_id: string;
	role?: CompanionSpeakerRole;
}

export interface CompanionAudioController {
	prepareSystemAudio(): Promise<void>;
	resumePlayback(): Promise<void>;
	connect(
		connection: {endpoint: string; token: string},
		systemConnection?: {endpoint: string; token: string},
		playback?: {participantIdentity: string},
	): Promise<void>;
	setPaused(paused: boolean): void;
	sendText(text: string): Promise<boolean>;
	disconnect(): void;
}

export interface CompanionSession {
	activity?: 'listening' | 'thinking' | 'speaking';
	session_id: string;
	audio_source: CompanionAudioSource;
	mode?: CompanionSessionMode;
	status: CompanionSessionStatus;
	speakers: Array<CompanionSpeaker>;
	scene_id?: string;
	error?: string;
	summary?: string;
}

export interface CompanionTranscript {
	session_id: string;
	segment_id: string;
	speaker_id: string;
	role?: CompanionSpeakerRole | 'ai';
	text: string;
	timestamp_ms: number;
	// false = ASR 中间结果; 不持久化, 定稿以同 segment_id 覆盖。
	final: boolean;
}

export interface CompanionSuggestion {
	session_id: string;
	suggestion_id: string;
	kind: string;
	text: string;
	final?: boolean;
	created_at: string;
}

export interface CompanionSessionStatePayload {
	activity?: 'listening' | 'thinking' | 'speaking';
	session_id: string;
	status: CompanionSessionStatus;
	speakers: Array<string>;
	scene_id?: string;
	error?: string;
	summary?: string;
}

export class CompanionSessionStore {
	session: CompanionSession | null = null;
	transcripts: Array<CompanionTranscript> = [];
	suggestions: Array<CompanionSuggestion> = [];
	isPanelOpen = false;
	isRecovering = false;
	isAiReplyPending = false;
	playbackBlocked = false;
	isDetachedSession = false;
	selectedAudioSource: CompanionAudioSource = {kind: 'microphone'};
	includeSystemAudio = false;
	selectedMode: CompanionSessionMode = 'assist';
	panelSetupStep: 'mode' | 'setup' = 'mode';
	isPausing = false;
	controlRevision = 0;
	inputMuted = false;
	inputLevel = 0;
	inputDeviceLabel = '';
	private audioController: CompanionAudioController | null = null;
	isStarting = false;
	isStopping = false;
	isSuggesting = false;
	isSceneUpdating = false;
	isLoadingScenes = false;
	pendingSpeakerIds: Set<string> = new Set();
	operationError: string | null = null;
	scenesError: string | null = null;
	scenes: Array<CompanionScene> = [];
	defaultSceneId: string | null = null;
	selectedSceneId: string | null = null;
	isPreferencesOpen = false;
	isLoadingPreferences = false;
	isSavingPreferences = false;
	preferencesLoaded = false;
	preferencesError: string | null = null;
	preferences: CompanionPreferences = {
		tone: 'natural',
		length: 'brief',
		nickname: '',
		background: '',
		auto_suggest: true,
	};
	private startRequestToken = 0;
	private stopRequestToken = 0;
	private scenesRequestToken = 0;
	private scenesLoaded = false;
	private pendingChannelId: string | null = null;

	constructor() {
		makeAutoObservable<this, 'audioController'>(this, {audioController: false}, {autoBind: true});
	}

	get hasActiveSession(): boolean {
		return (
			this.session != null &&
			(this.session.status === 'pending' ||
				this.session.status === 'starting' ||
				this.session.status === 'listening' ||
				this.session.status === 'paused' ||
				this.session.status === 'ending')
		);
	}

	get canControlSession(): boolean {
		return (
			this.session?.status === 'pending' ||
			this.session?.status === 'starting' ||
			this.session?.status === 'listening' ||
			this.session?.status === 'paused'
		);
	}

	openPanel(): void {
		this.isPanelOpen = true;
		if (!this.hasActiveSession && !this.isStarting) void this.recoverActiveSession();
	}

	closePanel(): void {
		this.isPanelOpen = false;
		this.isPreferencesOpen = false;
		this.panelSetupStep = 'mode';
	}

	togglePreferences(): void {
		this.isPreferencesOpen = !this.isPreferencesOpen;
		this.preferencesError = null;
		if (this.isPreferencesOpen) void this.loadPreferences();
	}

	closePreferences(): void {
		this.isPreferencesOpen = false;
		this.preferencesError = null;
	}

	async loadPreferences(force = false): Promise<boolean> {
		if (this.isLoadingPreferences || (this.preferencesLoaded && !force)) return true;
		this.isLoadingPreferences = true;
		this.preferencesError = null;
		try {
			const loaded = await CompanionCommands.getPreferences();
			runInAction(() => {
				this.preferences = loaded;
				this.preferencesLoaded = true;
			});
			return true;
		} catch (error) {
			runInAction(() => {
				this.preferencesError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(COMPANION_REQUEST_FAILED_DESCRIPTOR);
			});
			return false;
		} finally {
			runInAction(() => {
				this.isLoadingPreferences = false;
			});
		}
	}

	async savePreferences(next: CompanionPreferences): Promise<boolean> {
		if (this.isSavingPreferences) return false;
		this.isSavingPreferences = true;
		this.preferencesError = null;
		try {
			const saved = await CompanionCommands.savePreferences(next);
			runInAction(() => {
				this.preferences = saved;
				this.preferencesLoaded = true;
			});
			return true;
		} catch (error) {
			runInAction(() => {
				this.preferencesError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(COMPANION_REQUEST_FAILED_DESCRIPTOR);
			});
			return false;
		} finally {
			runInAction(() => {
				this.isSavingPreferences = false;
			});
		}
	}

	togglePanel(): void {
		if (this.isPanelOpen) this.closePanel();
		else this.openPanel();
	}

	setAudioController(controller: CompanionAudioController): void {
		this.audioController = controller;
	}

	selectAudioSource(source: CompanionAudioSource): void {
		if (!this.hasActiveSession && !this.isStarting) this.selectedAudioSource = source;
	}

	selectSystemAudio(enabled: boolean): void {
		if (!this.hasActiveSession && !this.isStarting) this.includeSystemAudio = enabled;
	}

	selectMode(mode: CompanionSessionMode): void {
		if (!this.hasActiveSession && !this.isStarting && this.selectedMode !== mode) {
			this.selectedMode = mode;
			this.scenesRequestToken += 1;
			this.isLoadingScenes = false;
			this.scenesLoaded = false;
			this.scenes = [];
			this.selectedSceneId = null;
			this.defaultSceneId = null;
		}
	}

	showModeSetup(mode: CompanionSessionMode): void {
		this.openPanel();
		if (!this.hasActiveSession && !this.isStarting) this.session = null;
		this.selectMode(mode);
		this.panelSetupStep = 'setup';
	}

	showModeSelect(): void {
		if (!this.hasActiveSession && !this.isStarting) this.panelSetupStep = 'mode';
	}

	/** 面板打字输入: 仅个人 talk 会话, 由 agent 的文本输入通道进入同一回复管线。 */
	async sendText(text: string): Promise<boolean> {
		const trimmed = text.trim();
		if (!trimmed || trimmed.length > 500) return false;
		if (this.session?.mode !== 'talk' || !['listening', 'thinking', 'speaking'].includes(this.session.status)) {
			return false;
		}
		return this.audioController?.sendText(trimmed) ?? false;
	}

	setInputState(muted: boolean, level: number, label: string): void {
		this.inputMuted = muted;
		this.inputLevel = level;
		this.inputDeviceLabel = label;
	}

	async recoverActiveSession(): Promise<void> {
		if (this.isRecovering || this.isStarting || this.hasActiveSession) return;
		const token = this.startRequestToken;
		this.isRecovering = true;
		try {
			const result = await CompanionCommands.listSessions();
			if (token !== this.startRequestToken || this.isStarting || this.hasActiveSession) return;
			const current = result.sessions.find(
				(item) => item.status && ['pending', 'starting', 'listening', 'paused', 'ending'].includes(item.status),
			);
			if (!current?.status) return;
			runInAction(() => {
				this.session = {
					session_id: current.session_id,
					status: current.status!,
					audio_source: current.audio_source,
					mode: current.mode === 'talk' ? 'talk' : 'assist',
					speakers: [],
					scene_id: current.scene_id ?? undefined,
				};
				this.selectedAudioSource = current.audio_source;
				this.selectedMode = current.mode === 'talk' ? 'talk' : 'assist';
				this.isDetachedSession = current.audio_source.kind === 'microphone';
			});
		} catch {
			/* A failed lookup must not prevent an explicit start attempt. */
		} finally {
			// 入口守卫已保证不并发；token 失配时也必须复位，否则 start 被永久锁死。
			runInAction(() => {
				this.isRecovering = false;
			});
		}
	}

	async start(
		audioSource: CompanionAudioSource = this.selectedAudioSource,
		sceneId?: string,
		mode: CompanionSessionMode = this.selectedMode,
	): Promise<boolean> {
		if (this.isStarting || this.isRecovering) return false;
		if (this.hasActiveSession) {
			this.openPanel();
			return true;
		}
		const token = ++this.startRequestToken;
		const includeSystemAudio = audioSource.kind === 'microphone' && mode === 'assist' && this.includeSystemAudio;
		const requestedSceneId = sceneId ?? this.selectedSceneId ?? undefined;
		this.session = null;
		this.isDetachedSession = false;
		this.isAiReplyPending = false;
		this.transcripts = [];
		this.suggestions = [];
		this.pendingSpeakerIds.clear();
		this.pendingChannelId = audioSource.kind === 'room' ? audioSource.channel_id : null;
		this.selectedAudioSource = audioSource;
		this.isStarting = true;
		this.isStopping = false;
		this.operationError = null;
		this.isPanelOpen = true;
		let createdSessionId: string | null = null;
		try {
			if (audioSource.kind === 'microphone' && !this.audioController)
				throw new Error('Microphone is not ready. Reopen companion and try again.');
			// Capture while the Start click still has browser user activation, before any API request.
			if (includeSystemAudio) await this.audioController!.prepareSystemAudio();
			if (token !== this.startRequestToken) return false;
			const result = await CompanionCommands.startSession({
				audioSource,
				mode,
				sceneId: requestedSceneId,
				...(includeSystemAudio ? {includeSystemAudio: true} : {}),
			});
			createdSessionId = result.session_id;
			if (token !== this.startRequestToken) {
				await CompanionCommands.stopSession(result.session_id);
				return false;
			}
			runInAction(() => {
				if (token !== this.startRequestToken) return;
				this.session = {
					session_id: result.session_id,
					audio_source: result.audio_source,
					mode: result.mode ?? mode,
					status: result.status,
					speakers: [],
					scene_id: requestedSceneId ?? this.defaultSceneId ?? undefined,
				};
				this.pendingChannelId = null;
			});
			if (token !== this.startRequestToken || !this.hasActiveSession) return false;
			if (audioSource.kind === 'microphone') {
				if (!result.connection) throw new Error('Microphone connection is unavailable');
				if (includeSystemAudio && !result.system_connection) throw new Error('System audio connection is unavailable');
				await this.audioController!.connect(
					result.connection,
					includeSystemAudio ? result.system_connection : undefined,
					mode === 'talk' ? {participantIdentity: `companion_${result.session_id}`} : undefined,
				);
			}
			return token === this.startRequestToken;
		} catch (error) {
			if (token === this.startRequestToken) this.audioController?.disconnect();
			if (createdSessionId) await CompanionCommands.stopSession(createdSessionId).catch(() => {});
			runInAction(() => {
				if (token !== this.startRequestToken) return;
				this.operationError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(COMPANION_REQUEST_FAILED_DESCRIPTOR);
				if (this.session) this.session.status = 'failed';
				this.pendingChannelId = null;
			});
			return false;
		} finally {
			runInAction(() => {
				if (token === this.startRequestToken) this.isStarting = false;
			});
		}
	}

	async handleAudioFailure(error: unknown, endSession = false): Promise<void> {
		const sessionId = this.session?.session_id;
		if (!sessionId || !this.hasActiveSession) return;
		const message =
			CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(COMPANION_REQUEST_FAILED_DESCRIPTOR);
		this.audioController?.setPaused(true);
		// A stopped sharing source requires a fresh user gesture. End cleanly instead of offering a broken resume.
		if (endSession || this.session?.status === 'pending' || this.session?.status === 'starting') await this.stop();
		else if (this.session?.status === 'listening') await this.setPaused(true);
		runInAction(() => {
			if (this.session?.session_id === sessionId) this.operationError = message;
		});
	}

	async setPaused(paused: boolean): Promise<boolean> {
		const session = this.session;
		if (!session || this.isPausing || !['listening', 'paused'].includes(session.status)) return false;
		const sessionId = session.session_id;
		this.isPausing = true;
		this.controlRevision += 1;
		this.operationError = null;
		if (paused) this.audioController?.setPaused(true);
		try {
			const result = await CompanionCommands.pauseSession(sessionId, paused);
			if (this.session?.session_id !== sessionId) return false;
			runInAction(() => {
				this.session!.status = result.status;
			});
			this.audioController?.setPaused(result.status === 'paused');
			return true;
		} catch (error) {
			if (this.session?.session_id === sessionId) {
				runInAction(() => {
					this.operationError = CompanionCommands.getCompanionCommandErrorMessage(error);
				});
				// Keep capture stopped when a pause cannot be confirmed.
			}
			return false;
		} finally {
			runInAction(() => {
				this.isPausing = false;
			});
		}
	}

	async stop(): Promise<boolean> {
		const session = this.session;
		if (!session || !this.hasActiveSession || session.status === 'ending') return false;
		const sessionId = session.session_id;
		const token = ++this.stopRequestToken;
		this.isStopping = true;
		this.controlRevision += 1;
		this.audioController?.disconnect();
		this.operationError = null;
		try {
			const result = await CompanionCommands.stopSession(sessionId);
			runInAction(() => {
				if (token !== this.stopRequestToken || this.session?.session_id !== sessionId) return;
				this.session.status = result.status;
				if (result.summary) this.session.summary = result.summary;
			});
			return token === this.stopRequestToken;
		} catch (error) {
			runInAction(() => {
				if (token !== this.stopRequestToken || this.session?.session_id !== sessionId) return;
				this.operationError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(COMPANION_REQUEST_FAILED_DESCRIPTOR);
			});
			return false;
		} finally {
			runInAction(() => {
				if (token === this.stopRequestToken) this.isStopping = false;
			});
		}
	}

	async markSpeaker(speakerId: string, role: CompanionSpeakerRole): Promise<boolean> {
		const sessionId = this.session?.session_id;
		if (!sessionId || !this.canControlSession) return false;
		this.pendingSpeakerIds.add(speakerId);
		this.operationError = null;
		try {
			await CompanionCommands.markSpeaker(sessionId, speakerId, role);
			runInAction(() => {
				if (this.session?.session_id !== sessionId) return;
				this.applySpeakerRole(speakerId, role);
			});
			return true;
		} catch (error) {
			runInAction(() => {
				if (this.session?.session_id !== sessionId) return;
				this.operationError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(COMPANION_REQUEST_FAILED_DESCRIPTOR);
			});
			return false;
		} finally {
			runInAction(() => this.pendingSpeakerIds.delete(speakerId));
		}
	}

	async setScene(sceneId: string): Promise<boolean> {
		const session = this.session;
		if (!session || !this.hasActiveSession) {
			this.selectedSceneId = sceneId;
			return true;
		}
		if (!this.canControlSession) return false;
		const sessionId = session.session_id;
		this.isSceneUpdating = true;
		this.operationError = null;
		try {
			const appliedSceneId = await CompanionCommands.setScene(sessionId, sceneId);
			runInAction(() => {
				this.selectedSceneId = appliedSceneId;
				if (this.session?.session_id === sessionId) this.session.scene_id = appliedSceneId;
			});
			return true;
		} catch (error) {
			runInAction(() => {
				if (this.session?.session_id !== sessionId) return;
				this.operationError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(COMPANION_REQUEST_FAILED_DESCRIPTOR);
			});
			return false;
		} finally {
			runInAction(() => {
				this.isSceneUpdating = false;
			});
		}
	}

	async resumePlayback(): Promise<void> {
		try {
			await this.audioController?.resumePlayback();
			this.playbackBlocked = false;
		} catch {
			this.playbackBlocked = true;
		}
	}
	async interrupt(): Promise<void> {
		const id = this.session?.session_id;
		if (!id || this.session?.mode !== 'talk') return;
		try {
			await CompanionCommands.interrupt(id);
		} catch (error) {
			runInAction(() => {
				this.operationError = CompanionCommands.getCompanionCommandErrorMessage(error);
			});
		}
	}

	async requestSuggestion(actionOrOptions?: string | CompanionSuggestRequestOptions): Promise<boolean> {
		const sessionId = this.session?.session_id;
		if (!sessionId || !this.canControlSession) return false;
		const options: CompanionSuggestRequestOptions =
			typeof actionOrOptions === 'string' ? {action: actionOrOptions} : (actionOrOptions ?? {});
		this.isSuggesting = true;
		this.operationError = null;
		try {
			const response = await CompanionCommands.requestSuggestion(sessionId, options);
			runInAction(() => {
				if (this.session?.session_id !== sessionId) return;
				if (
					response &&
					typeof response.suggestion === 'string' &&
					response.suggestion.trim() !== '' &&
					typeof response.suggestion_id === 'string' &&
					response.suggestion_id !== ''
				) {
					this.handleSuggestion({
						session_id: sessionId,
						suggestion_id: response.suggestion_id,
						kind: response.kind ?? options.action ?? 'Assist',
						text: response.suggestion,
						final: true,
						created_at: new Date().toISOString(),
					});
				}
			});
			return true;
		} catch (error) {
			runInAction(() => {
				if (this.session?.session_id !== sessionId) return;
				this.operationError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(COMPANION_REQUEST_FAILED_DESCRIPTOR);
			});
			return false;
		} finally {
			runInAction(() => {
				this.isSuggesting = false;
			});
		}
	}

	async loadScenes(force = false): Promise<boolean> {
		if (this.isLoadingScenes || (this.scenesLoaded && !force)) return true;
		const token = ++this.scenesRequestToken;
		this.isLoadingScenes = true;
		this.scenesError = null;
		try {
			const catalog = await CompanionCommands.listScenes(
				this.hasActiveSession ? (this.session?.mode ?? this.selectedMode) : this.selectedMode,
			);
			runInAction(() => {
				if (token !== this.scenesRequestToken) return;
				this.scenes = catalog.scenes;
				this.defaultSceneId = catalog.default_scene_id ?? null;
				this.scenesLoaded = true;
				const sceneIds = new Set(catalog.scenes.map((scene) => scene.id));
				const preferredSceneId = this.session?.scene_id ?? this.selectedSceneId ?? this.defaultSceneId;
				this.selectedSceneId =
					preferredSceneId && sceneIds.has(preferredSceneId) ? preferredSceneId : (catalog.scenes[0]?.id ?? null);
			});
			return token === this.scenesRequestToken;
		} catch (error) {
			runInAction(() => {
				if (token !== this.scenesRequestToken) return;
				this.scenesError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(COMPANION_REQUEST_FAILED_DESCRIPTOR);
			});
			return false;
		} finally {
			runInAction(() => {
				if (token === this.scenesRequestToken) this.isLoadingScenes = false;
			});
		}
	}

	handleTranscript(data: CompanionTranscript): void {
		if (this.session?.session_id !== data.session_id) return;
		const existingIndex = this.transcripts.findIndex((item) => item.segment_id === data.segment_id);
		if (existingIndex >= 0) {
			const existing = this.transcripts[existingIndex]!;
			// 迟到的 interim 不得覆盖已定稿文本。
			if (existing.final && !data.final) return;
			this.transcripts[existingIndex] = data;
		} else {
			this.transcripts.push(data);
			if (this.transcripts.length > COMPANION_TRANSCRIPT_LIMIT) {
				this.transcripts.splice(0, this.transcripts.length - COMPANION_TRANSCRIPT_LIMIT);
			}
		}
		if (this.session.mode === 'talk') {
			// talk 模式: 房间用户说完(final)后 AI 开始组织回复, 收到 AI 转写即结束。
			if (data.speaker_id === '0') this.isAiReplyPending = false;
			else if (data.final && data.speaker_id === '1') this.isAiReplyPending = true;
		}
		// 'ai' 角色仅用于字幕展示, 不得写入可标记的 self/customer 说话人角色。
		const speakerRole = data.role === 'ai' ? undefined : data.role;
		const speaker = this.session.speakers.find((item) => item.speaker_id === data.speaker_id);
		if (speaker) {
			if (speakerRole) speaker.role = speakerRole;
		} else {
			this.session.speakers.push({speaker_id: data.speaker_id, role: speakerRole});
		}
	}

	handleSuggestion(data: CompanionSuggestion): void {
		if (this.session?.session_id !== data.session_id) return;
		const isFinal = data.final ?? true;
		const normalized: CompanionSuggestion = {...data, final: isFinal};
		const existingIndex = this.suggestions.findIndex((item) => item.suggestion_id === data.suggestion_id);
		if (existingIndex >= 0) {
			const existing = this.suggestions[existingIndex]!;
			// 迟到的流式增量不得覆盖已定稿的完整建议。
			if ((existing.final ?? true) && !isFinal) return;
			this.suggestions[existingIndex] = normalized;
		} else {
			this.suggestions.push(normalized);
			if (this.suggestions.length > COMPANION_SUGGESTION_LIMIT) {
				this.suggestions.splice(0, this.suggestions.length - COMPANION_SUGGESTION_LIMIT);
			}
		}
	}

	handleSessionState(data: CompanionSessionStatePayload): void {
		if (this.session?.session_id !== data.session_id) return;
		const roles = new Map(this.session.speakers.map((speaker) => [speaker.speaker_id, speaker.role]));
		if (this.isPausing && (data.status === 'listening' || data.status === 'paused')) return;
		this.session.status = data.status;
		if (data.activity) this.session.activity = data.activity;
		this.audioController?.setPaused(data.status === 'paused' || data.status === 'ending' || !this.hasActiveSession);
		if (!this.hasActiveSession) {
			this.audioController?.disconnect();
			this.startRequestToken += 1;
			this.isStarting = false;
		}
		this.session.speakers = data.speakers.map((speakerId) => ({speaker_id: speakerId, role: roles.get(speakerId)}));
		this.session.scene_id = data.scene_id ?? this.session.scene_id;
		this.session.error = data.error;
		this.session.summary = data.summary ?? this.session.summary;
		if (data.scene_id) this.selectedSceneId = data.scene_id;
	}

	dismissSuggestion(suggestionId: string): void {
		this.suggestions = this.suggestions.filter((suggestion) => suggestion.suggestion_id !== suggestionId);
	}

	handleVoiceChannelLeft(channelId: string): void {
		const source = this.session?.audio_source;
		if ((source?.kind !== 'room' || source.channel_id !== channelId) && this.pendingChannelId !== channelId) return;
		if (this.session?.status === 'listening') void this.setPaused(true);
		else if (this.session?.status !== 'paused') {
			void this.stop();
			this.startRequestToken += 1;
			this.isStarting = false;
		}
		this.isPanelOpen = true;
	}

	reset(): void {
		this.controlRevision += 1;
		this.audioController?.disconnect();
		this.clearSession();
		this.selectedAudioSource = {kind: 'microphone'};
		this.includeSystemAudio = false;
		this.selectedMode = 'assist';
		this.panelSetupStep = 'mode';
		this.scenesRequestToken += 1;
		this.scenes = [];
		this.defaultSceneId = null;
		this.selectedSceneId = null;
		this.scenesLoaded = false;
		this.scenesError = null;
		this.isPreferencesOpen = false;
		this.isLoadingPreferences = false;
		this.isSavingPreferences = false;
		this.preferencesLoaded = false;
		this.preferencesError = null;
		this.preferences = {
			tone: 'natural',
			length: 'brief',
			nickname: '',
			background: '',
			auto_suggest: true,
		};
		this.isPanelOpen = false;
	}

	private applySpeakerRole(speakerId: string, role: CompanionSpeakerRole): void {
		if (!this.session) return;
		const speaker = this.session.speakers.find((item) => item.speaker_id === speakerId);
		if (speaker) speaker.role = role;
		else this.session.speakers.push({speaker_id: speakerId, role});
	}

	private clearSession(): void {
		this.startRequestToken += 1;
		this.stopRequestToken += 1;
		this.session = null;
		this.pendingChannelId = null;
		this.transcripts = [];
		this.suggestions = [];
		this.pendingSpeakerIds.clear();
		this.isStarting = false;
		this.isRecovering = false;
		this.isDetachedSession = false;
		this.isAiReplyPending = false;
		this.isStopping = false;
		this.isPausing = false;
		this.isSuggesting = false;
		this.isSceneUpdating = false;
		this.operationError = null;
	}
}

const companionSessionStore = new CompanionSessionStore();
AccountScopedWork.registerCancellation(() => companionSessionStore.reset());

export default companionSessionStore;
